import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {
  Card,
  Stack,
  Container,
  Grid,
  Drawer,
  Box,
  MenuItem,
  Select,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { toast } from 'react-toastify';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import 'react-toastify/dist/ReactToastify.css';
import { PauseCircleOutlined, PlayCircleOutline } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getMyTaskFilteredTask, getTaskById, updateTask } from '../../../services/taskService';
import useTrackerStartWithSurvey from '../../../hooks/useTrackerStartWithSurvey';
import ProjectAddTask from '../../projects/details/components/projectTaskList/ProjectAddTask';
import Loading from '../../../layouts/loading';
import NavigationTab from './NavigationTab';
import { DEFAULT_TABLE_PAGE_LIMIT } from '../../../Constants/table.constants';
import { boldHeader, MY_TASK_LIST_TABLE_COLUMNS, MY_TASK_LIST_TABLE_DEFAULT_PROPS } from '../constants';
import { fetchRunningTask, trackerRunningService } from '../../../services/trackerService';
import { useStyles } from '../styles/taskCardStyles';
import TableWrapper from '../../../common/TableWrapper';
import getStyles from '../../../utils/getStylesForTaskStatusSelect';
import Label from '../../../components/label';
import DueDateFiltersResult from './DueDateFiltersResult';
import { BILLABLE_OPTION } from '../../Report/constants';
import { isAdmin, isStakeholder } from '../../../utils/share';
import { isSurveyFlowCancelledError } from '../../../utils/surveyGate';
import { Loader } from '../../../components/loader';

// ----------------------------------------------------------------------

function MyTaskList() {
  const [openModal, setModalOpen] = useState(false);
  const [action, setAction] = useState('');
  const [selected, setSelected] = useState();
  const [selectedProject, setSelectedProject] = useState({});
  const [isDrawerLoading, setIsDrawerLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [columnFilters, setColumnFilters] = useState(Array.from(searchParams.keys()).map((item) => ({ id: item })));
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_TABLE_PAGE_LIMIT,
  });
  const classes = useStyles({ isOver: false });
  const accountData = useSelector((state) => state?.login?.user || null);
  const [runningTask, setRunningTask] = useState(null);
  // const [selectedStatus, setSelectedStatus] = useState('Not Started');
  const [isLoadingTimer, setIsLoadingTimer] = useState(false);
  const [currentTab, setCurrentTab] = useState(isAdmin() ? 'My Tasks Only' : '');
  const startTrackerWithSurvey = useTrackerStartWithSurvey();

  // const navigate = useNavigate();

  // const handleClearQuery = () => {
  //   navigate(window.location.pathname, { replace: true });
  // };

  const handleOpen = useCallback((projectName, projectId) => {
    setModalOpen(true);
    setSelectedProject({ name: projectName, id: projectId });
  }, []);

  const handleClose = useCallback(() => {
    setModalOpen(false);
    setIsDrawerLoading(false);
  }, []);

  const handleTableCellClick = useCallback(
    async (row) => {
      // Open drawer immediately
      setAction('update');
      setSelected(null); // Clear previous selection
      handleOpen(row.projects.projectName, row.projects.id);

      // Show loading inside drawer
      setIsDrawerLoading(true);

      try {
        const response = await getTaskById(row.id);
        const taskData = response?.data?.data;
        if (taskData) {
          setSelected({
            ...taskData,
            consumedHours: row.consumedHours,
          });
        } else {
          toast.error('Task data not found');
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsDrawerLoading(false);
      }
    },
    [handleOpen]
  );

  const {
    data: {
      count = 0,
      rows = [],
      MyTasksCount = 0,
      inProgressCount = 0,
      notStartedCount = 0,
      completedCount = 0,
      blockedCount = 0,
    } = {},
    isError,
    isRefetching,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting, currentTab],
    queryFn: async () => {
      try {
        const clFilter = [
          currentTab === 'My Tasks Only' ? { id: 'user.userName', value: `${accountData?.userName}` } : null,
          ...columnFilters,
        ].filter(Boolean);
        const response = await getMyTaskFilteredTask({
          start: pagination.pageIndex * pagination.pageSize,
          pageSize: pagination.pageSize,
          filters: JSON.stringify(clFilter ?? []),
          globalFilter,
          sorting: JSON.stringify(sorting ?? []),
          status: currentTab === 'My Tasks Only' ? '' : currentTab,
        });
        // handleClearQuery()
        return response?.data?.data;
      } catch (error) {
        toast.error('An error occurred while fetching the task list. Please try again later.');
        return { count: 0, rows: [] };
      }
    },
    placeholderData: keepPreviousData,
  });

  // Function to reset all filters
  const handleResetFilters = () => {
    // Clear URL parameters
    setSearchParams({});
    setColumnFilters((prev) =>
      prev.filter((item) => !['today', 'tomorrow', 'week', 'overdue', 'overrun'].includes(item.id))
    );
  };

  useEffect(() => {
    const fetchRunningTaskData = async () => {
      try {
        const runningTaskResponse = await fetchRunningTask();
        setRunningTask(runningTaskResponse?.data?.data);
      } catch (error) {
        toast.error(error?.response?.data?.message ?? error.message);
      }
    };

    fetchRunningTaskData();
  }, [refetch]);

  const handleStatusChange = async (event, taskId) => {
    const newStatus = event.target.value;
    try {
      await updateTask(taskId, { status: newStatus });
      refetch();
    } catch (error) {
      toast.error(error?.response?.data?.message ?? error.message);
    }
  };

  const handleStartTimer = async (e, task) => {
    e.stopPropagation();
    if (task?.userId !== accountData?.id) return;

    try {
      setIsLoadingTimer(true);
      const startTime = new Date().toISOString();
      const taskId = task?.id;
      const projectId = task?.projectId;
      await startTrackerWithSurvey({ startTime, taskId, projectId });
      setRunningTask(task);
      refetch();
      toast.info('Timer Started');
    } catch (error) {
      if (isSurveyFlowCancelledError(error)) {
        return;
      }

      const errMsg =
        error?.response?.data?.message || 'An error occurred while starting the timer. Please try again later.';
      toast.error(errMsg);
    } finally {
      setIsLoadingTimer(false);
    }
  };

  const handleStopTimer = async (e, task) => {
    e.stopPropagation();
    if (task?.userId !== accountData?.id) return;

    try {
      setIsLoadingTimer(true);
      const stopTime = new Date().toISOString();
      const taskId = task?.id;
      const projectId = task?.projectId;
      await trackerRunningService({ stopTime, taskId, projectId });
      setRunningTask(null);
      refetch();
      toast.info('Timer Paused');
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while pausing the timer. Please try again later.';
      toast.error(errMsg);
    } finally {
      setIsLoadingTimer(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'action',
        header: 'Action',
        enableSorting: false,
        enableColumnFilter: false,
        size: 50,
        Header: boldHeader('#454F5B'),
        Cell: ({ row }) => {
          const task = row.original;

          return (
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              className={classes?.customTableCell}
            >
              {runningTask?.id === task?.id ? (
                <Tooltip title="Pause timer" placement="top" arrow>
                  <IconButton
                    className={classes?.customTableCell}
                    sx={{ ml: 0 }}
                    onClick={(e) => !isStakeholder() && handleStopTimer(e, task)}
                    color="error"
                    disabled={!(accountData?.id === task?.userId)}
                  >
                    <PauseCircleOutlined />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Run timer in this task" placement="top" arrow>
                  <IconButton
                    className={classes?.customTableCell}
                    sx={{ ml: 0 }}
                    onClick={(e) => !isStakeholder() && handleStartTimer(e, task)}
                    color="success"
                    disabled={!(accountData?.id === task?.userId)}
                  >
                    <PlayCircleOutline />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      ...MY_TASK_LIST_TABLE_COLUMNS,
      {
        accessorKey: 'status',
        header: 'Status',
        // filterVariant: 'select',
        // filterSelectOptions: TASK_STATUS_LIST,
        enableColumnFilter: false,

        Header: boldHeader('#454F5B'),
        size: 100,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Select
              value={row.original.status === 'To Do' ? 'Not Started' : row.original.status}
              onChange={(event) => handleStatusChange(event, row.original.id)}
              onClick={(event) => {
                event.stopPropagation();
              }}
              fullWidth
              size="small"
              renderValue={(selected) => (
                <Box sx={{ ...getStyles(selected), padding: '2px 8px', borderRadius: '4px' }}>{selected}</Box>
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      '&.NotStarted': { color: '#161C24', backgroundColor: '#DFE3E8' },
                      '&.InProgress': { color: '#d49116', backgroundColor: '#fff3bb' },
                      '&.Completed': { color: '#188d1a', backgroundColor: '#b3ffb3' },
                      '&.Blocked': { color: 'red', backgroundColor: 'lightpink' },
                    },
                  },
                },
              }}
            >
              <MenuItem value="Not Started" className="NotStarted">
                Not Started
              </MenuItem>
              <MenuItem value="In Progress" className="InProgress">
                In Progress
              </MenuItem>
              <MenuItem value="Completed" className="Completed">
                Completed
              </MenuItem>
              <MenuItem value="Blocked" className="Blocked">
                Blocked
              </MenuItem>
            </Select>
          </Box>
        ),
      },
    ],
    [pagination, accountData, runningTask]
  );

  const TABS = [
    isAdmin() && {
      value: 'My Tasks Only',
      label: 'My Tasks Only',
      color: 'dark',
      count: MyTasksCount,
    },
    {
      value: 'Not Started',
      label: 'Not Started',
      color: 'default',
      count: notStartedCount,
    },
    {
      value: 'In Progress',
      label: 'In Progress',
      color: 'warning',
      count: inProgressCount,
    },
    {
      value: 'Completed',
      label: 'Completed',
      color: 'success',
      count: completedCount,
    },
    {
      value: 'Blocked',
      label: 'Blocked',
      color: 'error',
      count: blockedCount,
    },
  ].filter(Boolean);

  const table = useMaterialReactTable({
    columns,
    data: rows?.filter((task) => task?.taskName?.trim()?.toLowerCase() !== 'not aware') || [],
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    muiToolbarAlertBannerProps: isError ? { color: 'error', children: 'Error loading data' } : undefined,
    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      showSkeletons: isRefetching,
      showAlertBanner: isError,
    },
    rowCount: count,
    muiTableBodyRowProps: ({ row }) => ({
      sx: { backgroundColor: '#fff', cursor: 'pointer' },
      onClick: () => handleTableCellClick(row.original),
    }),
    ...MY_TASK_LIST_TABLE_DEFAULT_PROPS,

    renderTopToolbarCustomActions: ({ table }) => {
      const handleChangeTab = (event, newValue) => {
        setCurrentTab(newValue);
      };

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Tabs
            value={currentTab}
            onChange={handleChangeTab}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: 'black',
              },
              outline: 'none !important',
              minHeight: '40px',
              '& .MuiTab-root': {
                padding: '8px 16px',
                minHeight: '40px',
              },
            }}
          >
            {TABS.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                iconPosition="end"
                // sx={{ outline: 'none !important' }}
                sx={{
                  outline: 'none !important',
                  '&.Mui-selected': {
                    color: 'black',
                    fontWeight: 'bold',
                  },
                }}
                icon={
                  <Label variant={'soft'} color={tab.color}>
                    {tab.count}
                  </Label>
                }
              />
            ))}
          </Tabs>
          {['today', 'tomorrow', 'week', 'overdue', 'overrun'].some((filter) =>
            columnFilters.some((columnFilter) => columnFilter.id === filter)
          ) && (
            <DueDateFiltersResult
              filters={columnFilters}
              onFilters={handleResetFilters}
              onResetFilters={handleResetFilters}
              results={count}
              sx={{ p: 2.5, pt: 0 }}
            />
          )}
        </Box>
      );
    },
  });

  return (
    <>
      <Container maxWidth="4xl" sx={{ marginTop: '-26px' }}>
        <Loading open={isLoading || isLoadingTimer} />
        <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={2.5}>
          <NavigationTab />
        </Stack>
        <Card>
          <TableWrapper>
            <MaterialReactTable table={table} />
          </TableWrapper>
        </Card>
      </Container>
      <Grid>
        <Drawer PaperProps={{ sx: { width: '55%' } }} anchor="right" open={openModal} onClose={handleClose}>
          <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {isDrawerLoading ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Loader size={40} />
              </Box>
            ) : (
              <Box sx={{ height: '100%', overflow: 'auto', paddingBottom: '20px' }}>
                <ProjectAddTask
                  selectedTask={selected}
                  open={openModal}
                  project={selectedProject}
                  close={handleClose}
                  action={action}
                  onSubmit={refetch}
                />
              </Box>
            )}
          </Box>
        </Drawer>
      </Grid>
    </>
  );
}

export default React.memo(MyTaskList);
